import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const paths = [
  {
    step: '01',
    phase: '初學階段',
    title: '認識聲音，建立安全感',
    desc: '從接觸古箏的聲音開始，不急著彈曲子。讓手指習慣琴弦，讓耳朵熟悉音色，讓身體自然放鬆。這個階段最重要的，是讓學習本身成為一件安心的事。',
    color: 'bg-[#f5f0e8]',
    accent: '#c8a96e',
    keywords: ['零基礎歡迎', '不追趕進度', '建立自信'],
  },
  {
    step: '02',
    phase: '穩定練習階段',
    title: '讓音樂走進日常',
    desc: '開始有了自己喜歡的曲目，練習逐漸成為日常習慣的一部分。這個階段不求完美，重視的是規律與感受——每次撥弦，都是與自己的對話。',
    color: 'bg-[#eaede5]',
    accent: '#4a6741',
    keywords: ['規律學習', '自選曲目', '音樂入生活'],
  },
  {
    step: '03',
    phase: '深化與調整階段',
    title: '走出自己的聲音',
    desc: '技巧漸穩之後，開始探索自己的表達方式。不是為了表演，而是讓音樂成為你真正的語言。這個階段可以停留很久，因為走出自己的聲音，值得花時間。',
    color: 'bg-[#ede8e0]',
    accent: '#7a6248',
    keywords: ['個人化風格', '深度探索', '長期陪伴'],
  },
];

export default function LearningPathSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-[#faf8f5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="divider-gold"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Learning Path</span>
            <div className="divider-gold"></div>
          </div>
          <h2
            className="font-serif-tc text-[#2c2420] leading-[1.4]"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
          >
            學習路徑
          </h2>
          <p className="mt-4 text-[1.1875rem] leading-[1.75] text-[#7a6e62] max-w-xl mx-auto">
            沒有標準進度表，只有屬於你的節奏。<br className="hidden md:block" />
            每個階段都可以停留、調整、回看。
          </p>
        </div>

        {/* Path Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {paths.map((path, i) => (
            <div
              key={i}
              className={`${path.color} rounded-2xl p-8 md:p-10 relative overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              {/* Step Number */}
              <div
                className="text-[5rem] font-bold leading-none mb-6 select-none"
                style={{ color: path.accent, opacity: 0.15 }}
              >
                {path.step}
              </div>

              <span className="inline-block px-3 py-1 rounded-full text-[0.8125rem] font-medium mb-4"
                style={{ backgroundColor: `${path.accent}20`, color: path.accent }}>
                {path.phase}
              </span>

              <h3
                className="font-serif-tc text-[#2c2420] mb-4 leading-[1.5]"
                style={{ fontSize: '1.375rem' }}
              >
                {path.title}
              </h3>

              <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] mb-8">
                {path.desc}
              </p>

              {/* Keywords */}
              <div className="flex flex-wrap gap-2">
                {path.keywords.map((kw, j) => (
                  <span
                    key={j}
                    className="px-3 py-1 rounded-full text-[0.875rem] border"
                    style={{ borderColor: `${path.accent}40`, color: path.accent }}
                  >
                    {kw}
                  </span>
                ))}
              </div>

              {/* Connector dot - not on last */}
              {i < paths.length - 1 && (
                <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                  <div className="w-6 h-6 rounded-full bg-[#faf8f5] border-2 border-[#d4c4a8] flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[#b8a07a]"></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={`text-center mt-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '0.5s' }}>
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 text-[1rem] font-medium text-[#7a6248] hover:text-[#5a4a3a] transition-colors cursor-pointer"
          >
            查看完整課程說明
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
